import { useNavigate, useLocation } from "react-router-dom";
import { ArrowLeft, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import AdminNotificationBell from "@/components/AdminNotificationBell";
import { ThemeToggle } from "@/components/ThemeToggle";
import BrandLogo from "@/components/BrandLogo";
import { useCallback } from "react";
import { useAdminCommandPalette } from "@/components/AdminCommandPalette";
import EnvironmentToggle from "@/components/admin/EnvironmentToggle";

const ROUTE_TITLES: Record<string, string> = {
  "/admin/dashboard": "Dashboard",
  "/admin/users": "Users",
  "/admin/kyc": "KYC Queue",
  "/admin/name-changes": "Name Change Requests",
  "/admin/verification-tiers": "Verification Tiers",
  "/admin/activation-queue": "Activation Queue",
  "/admin/orders": "Orders",
  "/admin/transactions": "Transactions",
  "/admin/exchanges": "Exchanges",
  "/admin/fiat-deposits": "Fiat Deposits",
  "/admin/fiat-withdrawals": "Fiat Withdrawals",
  "/admin/fiat-settings": "Fiat Settings",
  "/admin/exchange-wallets": "Exchange Wallets",
  "/admin/crypto-deposits": "Crypto Deposits",
  "/admin/crypto-withdrawals": "Crypto Withdrawals",
  "/admin/crypto-settings": "Crypto Settings",
  "/admin/spot-markets": "Spot Markets",
  "/admin/spot-orders": "Spot Orders",
  "/admin/spot-trades": "Spot Trades",
  "/admin/spot-settings": "Spot Settings",
  "/admin/ai-market-maker": "AI Market Maker",
  "/admin/futures-contracts": "Futures Contracts",
  "/admin/futures-orders": "Futures Orders",
  "/admin/futures-positions": "Futures Positions",
  "/admin/futures-settings": "Futures Settings",
  "/admin/p2p": "P2P Dashboard",
  "/admin/p2p-merchants": "P2P Merchants",
  "/admin/p2p-orders": "P2P Orders",
  "/admin/p2p-disputes": "P2P Disputes",
  "/admin/p2p-settings": "P2P Settings",
  "/admin/copy-trading": "Copy Trading",
  "/admin/virtual-cards": "Virtual Cards",
  "/admin/virtual-card-settings": "Card Settings",
  "/admin/token-config": "Token Config",
  "/admin/staking-plans": "Staking Plans",
  "/admin/legacy-notes": "Notes Market",
  "/admin/time-boxes": "Time Boxes",
  "/admin/earn-projects": "Earn Projects",
  "/admin/token-sale-offerings": "Token Sale",
  "/admin/token-sale-purchases": "Token Purchases",
  "/admin/demo-settings": "Demo Settings",
  "/admin/marketing": "Creator Program",
  "/admin/marketer-hq": "Marketer HQ",
  "/admin/ambassador-program": "Ambassador Program",
  "/admin/referral-settings": "Referral Settings",
  "/admin/referral-commissions": "Commissions",
  "/admin/referral-leaderboard": "Referral Leaderboard",
  "/admin/fees": "Platform Fees",
  "/admin/fees/network": "Network Fees",
  "/admin/fees/rules": "Fee Rules",
  "/admin/fees/log": "Fee Change Log",

  "/admin/modules": "Modules",
  "/admin/notification-templates": "Notifications",
  "/admin/integrations": "Integrations",
  "/admin/branding": "Branding",
  "/admin/landing-page": "Landing Page",
  "/admin/data-migration": "Data Migration",
  "/admin/staff": "Staff Users",
  "/admin/staff/roles": "Staff Roles",
  "/admin/staff/departments": "Departments",
  "/admin/support": "Support",
  "/admin/security-events": "Security Events",
  "/admin/captcha-settings": "CAPTCHA Security",
  "/admin/content": "Content",
  "/admin/context-faqs": "Context FAQs",
  "/admin/audit-logs": "Audit Logs",
  "/admin/my-settings": "My Settings",
  "/admin/my-security": "Security",
  "/admin/more": "More",
};

const ROOT_ROUTES = ["/admin/dashboard", "/admin/users", "/admin/transactions", "/admin/spot-markets", "/admin/more"];

const FALLBACK_PARENTS: [string, string][] = [
  ["/admin/users/", "/admin/users"],
  ["/admin/virtual-cards/", "/admin/virtual-cards"],
  ["/admin/staking-plans/", "/admin/staking-plans"],
  ["/admin/staff/", "/admin/staff"],
  ["/admin/p2p", "/admin/more"],
  ["/admin/spot-", "/admin/more"],
  ["/admin/futures-", "/admin/more"],
];

function getFallbackParent(pathname: string): string {
  for (const [prefix, parent] of FALLBACK_PARENTS) {
    if (pathname.startsWith(prefix)) return parent;
  }
  return "/admin/dashboard";
}

export default function AdminMobileHeader() {
  const navigate = useNavigate();
  const location = useLocation();
  const cmdPalette = useAdminCommandPalette();

  const isRoot = ROOT_ROUTES.some((r) => location.pathname === r);
  const title =
    ROUTE_TITLES[location.pathname] ||
    Object.entries(ROUTE_TITLES).find(([prefix]) => location.pathname.startsWith(prefix + "/"))?.[1] ||
    "Admin";

  const handleBack = useCallback(() => {
    const idx = (window.history.state as any)?.idx;
    if (typeof idx === "number" && idx > 0) {
      navigate(-1);
    } else {
      navigate(getFallbackParent(location.pathname), { replace: true });
    }
  }, [navigate, location.pathname]);

  return (
    <header className="sticky top-0 z-40 glass-strong border-b border-border/50 h-14 flex items-center justify-between px-3 pt-[env(safe-area-inset-top)]">
      <div className="flex items-center min-w-0">
        <div className="w-10 shrink-0 flex items-center justify-center">
          {!isRoot && (
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={handleBack}>
              <ArrowLeft className="h-4 w-4" />
            </Button>
          )}
        </div>
        {isRoot ? (
          <div className="flex items-center gap-2">
            <BrandLogo className="h-7 max-w-[130px] object-contain" />
          </div>
        ) : (
          <h1 className="text-sm font-semibold text-foreground truncate">{title}</h1>
        )}
      </div>
      <div className="flex items-center gap-0.5">
        <EnvironmentToggle compact />
        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => cmdPalette.setOpen(true)}>
          <Search className="h-4 w-4" />
        </Button>
        <ThemeToggle />
        <AdminNotificationBell />
      </div>
    </header>
  );
}
